import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import { Provider } from 'react-redux';
import EventEmitter from 'eventemitter3';
import { validateClientConfig, getAppConfig } from './config';
import * as Utils from './utils';
import * as store from './store/index';
import * as appStore from './store/app';
import Session from './session';
import './lib';
import './polyfills';
import { AUTO_PLAY_BEHAVIOUR, API_KEY_JSON_URlS, APP_EVENTS } from './constants';
import { decryptApiKeyJson } from './utils/encryption';
import PostMessageHandler from './postMessageHandler';
import { checkDomainConfiguration } from './utils';

class CleoClient extends EventEmitter {
  constructor(config = {}, callback = Utils.noop) {
    super();
    const [success, message] = validateClientConfig(config);
    if (!success) {
      callback(message);
      return;
    }
    this.callback = callback;
    this.bootstrap(config);
  }
  async bootstrap(config) {
    this.appConfig = getAppConfig(config);
    this.clientConfig = config;
    const apiKeyJson = await this.fetchApiKeyJson(this.appConfig.apiJsonKey);
    this.store = store.configure(this);
    this.store.dispatch(appStore.setAppConfig({ config: this.appConfig, apiKeyJson: apiKeyJson }));
    this.render();
    if (apiKeyJson) {
      var domainAllowed = checkDomainConfiguration(apiKeyJson);
      if (!domainAllowed) {
        this.store.dispatch(appStore.setAppError({ msg: 'Something went wrong', act: 'domain_not_allowed' }));
        return;
      }
      this.loadDependencies(null, (err) => {
        if (err) {
          this.store.dispatch(appStore.setAppError({ msg: 'Something went wrong', act: 'sdk_launch', err }));
          this.callback(err);
          return;
        }
        this.checkVideoAutoPlay(config, () => {
          this.postMessageHandler = new PostMessageHandler(this, this.store);
          this.emit(APP_EVENTS.ENV_READY);
          //if rendered in iframe create session will not be invoked directly ,
          // will be called from parent page after setting all required configurations
          if (!this.postMessageHandler.isRenderedInIframe() || config.skipIConfig) this.createSession();
        });
      });
    } else {
      this.store.dispatch(
        appStore.setAppError({ msg: 'Something went wrong', act: 'sdk_launch', err: 'failed to fetch api key json' }),
      );
    }
  }

  createSession() {
    this.session = new Session(this, this.store);
    this.session.handleUserAuth();
    this.callback();
  }

  async fetchApiKeyJson(apiKey, cb) {
    var fetchResponse;
    let env = process.env.REACT_APP_API_ENV ? `${process.env.REACT_APP_API_ENV}/` : '';
    if (env === 'prod/') env = '';
    var i = 0;
    for (i = 0; i < API_KEY_JSON_URlS.length; i++) {
      const url = `${API_KEY_JSON_URlS[i]}/c/apikey/${apiKey}/${env}web.json`;
      try {
        fetchResponse = await fetch(url);
        if (fetchResponse) {
          break;
        }
      } catch (e) {
        console.error('fetchApiKeyJson error', e);
      }
    }
    if (fetchResponse) {
      const textResponse = await fetchResponse.arrayBuffer();
      const imageUrl = `${API_KEY_JSON_URlS[i]}/c/apikey/${apiKey}/${env}logo.png`;
      const decryptedResponse = await decryptApiKeyJson(new Uint8Array(textResponse), imageUrl);
      if (decryptedResponse) {
        return decryptedResponse;
      }
      return null;
    }
    return null;
  }

  async checkVideoAutoPlay(config, callback) {
    if (this.appConfig.castMode || this.appConfig.puppet) {
      this.store.dispatch(appStore.setAutoPlayBehavior(AUTO_PLAY_BEHAVIOUR.ALLOW));
      callback();
      return;
    }
    const autoPlay = this.store.getState().app.apiJsonConfig.autoPlay;

    if (autoPlay === AUTO_PLAY_BEHAVIOUR.NEVER) {
      this.store.dispatch(appStore.setAutoPlayBehavior(AUTO_PLAY_BEHAVIOUR.NEVER));
      callback();
      return;
    }
    let autoPlayResult = await Utils.checkAutoPlay({
      muted: false,
      fallbackmute: autoPlay === AUTO_PLAY_BEHAVIOUR.WITHOUT_SOUND,
    });
    console.warn('autoplay settings', autoPlayResult, autoPlay);
    this.store.dispatch(appStore.setAutoPlayBehavior(autoPlayResult));
    callback();
  }

  async loadDependencies(dependencies, callback = Utils.noop) {
    if (!dependencies) {
      dependencies = ['sw'];
      dependencies.push('livesdk');
      if (this.appConfig.comments) {
        dependencies.push('chat');
      }
      if (this.appConfig.screenshot) {
        dependencies.push('screenshot');
      }
      if (this.appConfig.castMode) {
        dependencies = ['sw'];
      }
    }
    dependencies = dependencies.map((dep) => `REACT_APP_${dep.toUpperCase()}_PATH`);
    const livesdk = this.store.getState().app.apiKeyJson?.apis?.livesdk;
    const dependencyLoadPromises = dependencies.map((dep) => {
      if (dep === 'REACT_APP_LIVESDK_PATH' && !livesdk) {
        dep = 'REACT_APP_LIGHTLIVESDK_PATH';
      }
      if (dep === 'REACT_APP_SW_PATH') {
        const url = `${window.location.protocol}//${window.location.hostname}${
          window.location.port ? ':' + window.location.port : ''
        }/${process.env[dep]}?v=${process.env.VERSION}`;

        return Utils.loadJs(url);
      }

      return Utils.loadJs(`${process.env[dep]}?v=${process.env.VERSION}`);
    });
    try {
      await Promise.all(dependencyLoadPromises);
    } catch (err) {
      callback('Failed to load dependencies');
      return;
    }
    callback();
  }

  render() {
    ReactDOM.render(
      <Provider store={this.store}>
        <App ctrl={this} />
      </Provider>,
      document.getElementById(this.appConfig.contEl),
    );
  }
  static get commitHash() {
    return process.env.COMMIT_HASH;
  }
}

window.CleoClient = CleoClient;
